import { type ReactNode, useId } from 'react'

import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import type { SxProps, Theme } from '@mui/material/styles'

import { ExpandMore } from '@mxenabled/mx-icons'

import { ThemedSVGImage } from './chartblocks/ThemedSVGImage'
import { ReactComponent as EmergencySavingsGraphic } from './shared/images/DesignateEmergencySavings.svg'

// Hoisted so the default `graphic` prop keeps a stable reference across renders.
// Wrapped with ThemedSVGImage to apply theme colors to SVG elements with brand class names.
// Marked as decorative (aria-hidden) since the text/label conveys the meaning.
const DEFAULT_GRAPHIC = (
  <ThemedSVGImage
    divProps={{ 'aria-hidden': true, style: { display: 'block', width: '100%' } }}
    image={EmergencySavingsGraphic}
  />
)

export interface DesignateEmergencySavingsOption {
  value: string
  label: string
}

const EMPTY_OPTIONS: DesignateEmergencySavingsOption[] = []

// Hoisted to keep a stable reference (the Select re-renders otherwise).
//
// mx-icons `ExpandMore` forwards `sx` but drops the `className` MUI hands its
// `IconComponent` — so it never receives the `.MuiSelect-icon` positioning
// class. We position it ourselves to match Figma: the chevron's right edge sits
// 12px from the field's right border, vertically centered.
const SelectChevron = () => (
  <ExpandMore
    size={24}
    sx={{ pointerEvents: 'none', position: 'absolute', right: 12, top: 'calc(50% - 12px)' }}
  />
)

interface DesignateEmergencySavingsCardProps {
  /** Floating label on the select field. Default "Emergency savings account". */
  selectLabel?: string
  /** Text shown in the empty (no-selection) state. Default "Select". */
  placeholder?: string
  /** Selectable accounts. Empty by default (the placeholder-only state). */
  options?: DesignateEmergencySavingsOption[]
  /** Selected account value. Default "" (no selection). */
  value?: string
  /** Selection change handler. */
  onChange?: (value: string) => void
  /** Illustration graphic. Defaults to the emergency-savings illustration. */
  graphic?: ReactNode
  sx?: SxProps<Theme>
}

/**
 * Designate Emergency Savings Account content: an illustration (piggy bank under
 * an umbrella) above an "Emergency savings account" select showing the empty
 * "Select" state.
 *
 * Uses the repo's standard MUI `Select` (FormControl + InputLabel + MenuItem)
 * with the `ExpandMore` chevron, matching existing selects like AccountDetailEdit.
 * Figma: Designate Emergency Savings Account Content (Full variant). Scope is the
 * Content section only.
 */
const DesignateEmergencySavingsCard = ({
  graphic = DEFAULT_GRAPHIC,
  onChange,
  options = EMPTY_OPTIONS,
  placeholder = 'Select',
  selectLabel = 'Emergency savings account',
  value = '',
  sx,
}: DesignateEmergencySavingsCardProps) => {
  const labelId = useId()
  return (
    <Stack sx={sx}>
      {/* Illustration */}
      <Box sx={{ width: '100%' }}>{graphic}</Box>

      {/* Emergency savings account select */}
      <Box sx={{ pb: 12, pt: 24, px: 16 }}>
        <FormControl fullWidth={true}>
          {/* Figma label: typography/body2, text/secondary. */}
          <InputLabel id={labelId} shrink={true} sx={{ color: 'text.secondary' }}>
            {selectLabel}
          </InputLabel>
          <Select
            displayEmpty={true}
            IconComponent={SelectChevron}
            label={selectLabel}
            labelId={labelId}
            onChange={(event: SelectChangeEvent) => onChange?.(event.target.value)}
            // Figma value: typography/body1 (15/20), text/primary.
            sx={{
              '& .MuiSelect-select': {
                color: 'text.primary',
                fontSize: 15,
                lineHeight: '20px',
              },
            }}
            value={value}
          >
            <MenuItem disabled={true} value="">
              {placeholder}
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Stack>
  )
}

export default DesignateEmergencySavingsCard
